import { HttpClient } from '@angular/common/http';
import { Injectable, Signal, computed, effect, inject, signal } from '@angular/core';
import { catchError, of } from 'rxjs';
import { KeycloakPasswordAuthService } from './auth/keycloak-password-auth.service';

export type GameKey = '2048' | 'quiz' | 'number-match';

export interface GameScoreSummaryDto {
  gameKey: GameKey;
  agencyId: string | null;
  myBest: number | null;
  agencyBest: number | null;
  agencyBestUserId: string | null;
  agencyBestOwnerName: string | null;
}

const GAME_KEYS: readonly GameKey[] = ['2048', 'quiz', 'number-match'];
const EMPTY_SUMMARY = (gameKey: GameKey): GameScoreSummaryDto => ({
  gameKey,
  agencyId: null,
  myBest: null,
  agencyBest: null,
  agencyBestUserId: null,
  agencyBestOwnerName: null,
});

function emptyState(): Record<GameKey, GameScoreSummaryDto> {
  return {
    '2048': EMPTY_SUMMARY('2048'),
    quiz: EMPTY_SUMMARY('quiz'),
    'number-match': EMPTY_SUMMARY('number-match'),
  };
}

@Injectable({ providedIn: 'root' })
export class GameScoresService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(KeycloakPasswordAuthService);
  private readonly summaries = signal<Record<GameKey, GameScoreSummaryDto>>(emptyState());
  private readonly pending = new Map<GameKey, number>();
  private readonly URL = '/api/v1/me/game-scores';

  constructor() {
    effect(() => {
      const sub = this.auth.profile()?.sub;
      if (!sub) {
        this.summaries.set(emptyState());
        this.pending.clear();
        return;
      }
      this.load();
    }, { allowSignalWrites: true });
  }

  summary(gameKey: GameKey): Signal<GameScoreSummaryDto> {
    return computed(() => this.summaries()[gameKey]);
  }

  submitIfHigher(gameKey: GameKey, score: number): void {
    const normalized = Math.floor(score);
    if (!Number.isFinite(normalized) || normalized <= 0) return;
    if (!this.currentAgencyId()) return;

    const current = this.summaries()[gameKey];
    const pending = this.pending.get(gameKey) ?? 0;
    if ((current.myBest ?? 0) >= normalized || pending >= normalized) return;

    this.pending.set(gameKey, normalized);
    this.http.put<GameScoreSummaryDto>(`${this.URL}/${encodeURIComponent(gameKey)}`, { score: normalized })
      .subscribe({
        next: (resp) => {
          this.merge(resp);
          this.pending.delete(gameKey);
        },
        error: () => {
          this.pending.delete(gameKey);
        },
      });
  }

  private load(): void {
    this.http.get<GameScoreSummaryDto[]>(this.URL)
      .pipe(catchError(() => of<GameScoreSummaryDto[]>([])))
      .subscribe((rows) => {
        const next = emptyState();
        for (const row of rows ?? []) {
          if (row?.gameKey && GAME_KEYS.includes(row.gameKey)) next[row.gameKey] = row;
        }
        this.summaries.set(next);
      });
  }

  private merge(row: GameScoreSummaryDto): void {
    if (!row?.gameKey || !GAME_KEYS.includes(row.gameKey)) return;
    this.summaries.update((all) => ({ ...all, [row.gameKey]: row }));
  }

  private currentAgencyId(): string | null {
    const claims = this.auth.rawClaims?.() ?? null;
    const direct = typeof claims?.['agency_id'] === 'string' ? claims['agency_id'] as string : null;
    if (direct) return direct;
    const list = claims?.['agency_ids'] ?? claims?.['agencyIds'];
    return Array.isArray(list) && typeof list[0] === 'string' ? list[0] : null;
  }
}
